// Opt-in tracing for board drag-and-drop.
//
// Off by default — zero console output in normal use. Turn it on with either:
//   • append ?dragdebug=1 to the board URL, or
//   • localStorage.setItem('boardDragDebug', '1') then reload
//
// Every traced line is also buffered on `window.__boardDragTrace`, so a whole
// gesture can be handed over in one paste:
//   copy(__boardDragTrace.join('\n'))          // Chrome devtools
//   __boardDragTrace.join('\n')                // anywhere else
//
// Consecutive identical lines are collapsed. React StrictMode invokes state
// updaters twice in development, and some of these trace points sit inside
// one, so without collapsing every such line would appear doubled.

const BUFFER_LIMIT = 500;

let enabled: boolean | null = null;
let origin = 0;

/** Ms since the first traced line. Makes render-timing gaps visible. */
function elapsed(): string {
  const now = performance.now();
  if (!origin) origin = now;
  return `${Math.round(now - origin)}ms`.padStart(7);
}

function isEnabled(): boolean {
  if (enabled === null) {
    try {
      enabled =
        new URLSearchParams(window.location.search).get('dragdebug') === '1' ||
        window.localStorage.getItem('boardDragDebug') === '1';
    } catch {
      // SSR, or storage blocked by browser settings.
      enabled = false;
    }
  }
  return enabled;
}

function buffer(): string[] {
  const w = window as unknown as { __boardDragTrace?: string[] };
  if (!w.__boardDragTrace) w.__boardDragTrace = [];
  return w.__boardDragTrace;
}

const format = (value: unknown) => {
  if (value === undefined) return '∅';
  if (value === null) return 'null';
  return String(value);
};

/**
 * Emits one compact `key=value` line, e.g.
 *   [board-dnd] over active=k1 over=k4 overType=card col=c2 below=true slot=2
 */
export function dragTrace(event: string, detail: Record<string, unknown>) {
  if (!isEnabled()) return;

  const pairs = Object.entries(detail)
    .map(([k, v]) => `${k}=${format(v)}`)
    .join(' ');
  const body = `${event.padEnd(6)} ${pairs}`;

  const lines = buffer();
  // Compare without the timestamp, so StrictMode's doubled call collapses.
  if (lines[lines.length - 1]?.endsWith(body)) return;
  const line = `[board-dnd] ${elapsed()}  ${body}`;
  lines.push(line);
  if (lines.length > BUFFER_LIMIT) lines.shift();

  console.log(line);
}
